import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageSquare, Building2, Users, Search, Loader2 } from 'lucide-react';
import {
  fetchOrganizationsForMessaging,
  toggleNewMessageModal,
  setCurrentConversation,
  clearAvailableOrganizations
} from '../store/slices/messagingSlice';

const NewMessageModal = ({ isOpen, onClose, onConversationSelect }) => {
  const dispatch = useDispatch();
  const { 
    availableOrganizations, 
    availableOrgsStatus, 
    error 
  } = useSelector(state => state.messaging);

  const [searchTerm, setSearchTerm] = React.useState('');

  useEffect(() => {
    if (isOpen && availableOrgsStatus === 'idle') {
      dispatch(fetchOrganizationsForMessaging());
    }
  }, [isOpen, availableOrgsStatus, dispatch]);

  useEffect(() => {
    if (!isOpen) {
      dispatch(clearAvailableOrganizations());
      setSearchTerm('');
    }
  }, [isOpen, dispatch]);

  const handleOrganizationSelect = (orgId) => {
    if (onConversationSelect) {
      onConversationSelect(orgId);
    } else {
      dispatch(setCurrentConversation(orgId));
    }
    dispatch(toggleNewMessageModal(false));
    onClose();
  };

  const handleClose = () => {
    dispatch(toggleNewMessageModal(false));
    onClose();
  };

  // Filter organizations based on search term
  const filteredOrganizations = availableOrganizations.filter(org =>
    org.org_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", duration: 0.4 }}
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden border border-gray-100"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <MessageSquare className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-white">New Message</h2>
                  <p className="text-blue-100 text-sm">Start a conversation</p>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-white" />
              </motion.button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Search Bar */}
            {availableOrgsStatus === 'succeeded' && availableOrganizations.length > 0 && (
              <div className="relative mb-6">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search organizations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                />
              </div>
            )}

            {/* Loading State */}
            {availableOrgsStatus === 'loading' && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="relative">
                  <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
                  <div className="absolute inset-0 rounded-full border-2 border-blue-100"></div>
                </div>
                <p className="text-gray-600 text-sm mt-4">Loading organizations...</p>
              </div>
            )}

            {/* Error State */}
            {availableOrgsStatus === 'failed' && (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <X className="h-8 w-8 text-red-500" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Unable to Load Organizations</h3>
                <p className="text-gray-500 text-sm mb-4">
                  There was an error loading your available organizations.
                </p>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => dispatch(fetchOrganizationsForMessaging())}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  Try Again
                </motion.button>
              </div>
            )}

            {/* Organizations List */}
            {availableOrgsStatus === 'succeeded' && (
              <div className="space-y-3 max-h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                {filteredOrganizations.length === 0 ? (
                  <div className="text-center py-12">
                    {searchTerm ? (
                      <>
                        <Search className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Results Found</h3>
                        <p className="text-gray-500 text-sm">
                          No organizations match "{searchTerm}"
                        </p>
                      </>
                    ) : availableOrganizations.length === 0 ? (
                      <>
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Building2 className="h-8 w-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Organizations Available</h3>
                        <p className="text-gray-500 text-sm">
                          You need active loads with organizations to start conversations.
                        </p>
                      </>
                    ) : null}
                  </div>
                ) : (
                  <AnimatePresence>
                    {filteredOrganizations.map((org, index) => (
                      <motion.button
                        key={org.org_id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ delay: index * 0.05 }}
                        whileHover={{ scale: 1.02, y: -2 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleOrganizationSelect(org.org_id)}
                        className="w-full p-4 text-left bg-white hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 border border-gray-200 hover:border-blue-300 rounded-xl transition-all duration-300 group shadow-sm hover:shadow-md"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="flex-shrink-0">
                              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                                org.org_type === 'customer' 
                                  ? 'bg-blue-100 text-blue-600 group-hover:bg-blue-200' 
                                  : 'bg-green-100 text-green-600 group-hover:bg-green-200'
                              } transition-colors duration-300`}>
                                {org.org_type === 'customer' ? (
                                  <Building2 className="h-6 w-6" />
                                ) : (
                                  <Users className="h-6 w-6" />
                                )}
                              </div>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-gray-900 group-hover:text-blue-900 transition-colors truncate">
                                {org.org_name}
                              </p>
                              <p className="text-sm text-gray-500 capitalize flex items-center mt-1">
                                <span className={`w-2 h-2 rounded-full mr-2 ${
                                  org.org_type === 'customer' ? 'bg-blue-400' : 'bg-green-400'
                                }`}></span>
                                {org.org_type}
                              </p>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 group-hover:from-blue-200 group-hover:to-blue-300 transition-all duration-300">
                              {org.active_loads_count}
                              <span className="ml-1 hidden sm:inline">
                                load{org.active_loads_count !== 1 ? 's' : ''}
                              </span>
                            </div>
                          </div>
                        </div>
                      </motion.button>
                    ))}
                  </AnimatePresence>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleClose}
              className="w-full px-4 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-sm"
            >
              Cancel
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NewMessageModal;